import { useEffect, useRef } from 'react';
import { useDevice } from './useDevice';
import { useTenant } from './useTenant';

const HEADER_ID = 'top-header';
const FALLBACK_HEADER_HEIGHT = 80;

const getHeaderHeight = () => {
  const header = document.getElementById(HEADER_ID);
  return header?.getBoundingClientRect().height || FALLBACK_HEADER_HEIGHT;
};

const prefersReducedMotion = () =>
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

type ScrollToStageOptions = {
  /** Also scroll on the Global (GWAT) tenant. DealsAway always scrolls. */
  scrollOnGwat?: boolean;
};

/**
 * Mobile only: when the stage mounts (the user arrives at it), scroll so the
 * stage container starts directly below the sticky app header, skipping the
 * repeated stage indicator / trip summary block above it.
 *
 * DealsAway scrolls on every stage that uses this hook (Rooms and Payment; not
 * Traveller, which is the first DealsAway step). GWAT scrolls only on stages
 * that opt in with `scrollOnGwat` (Payment). Desktop never scrolls.
 *
 * The decision is taken once, at mount. Re-renders, validation changes and
 * viewport resizes never trigger a scroll.
 *
 * Attach the returned ref to the stage's outermost container.
 */
const useScrollToStageOnMobile = <T extends HTMLElement = HTMLDivElement>({
  scrollOnGwat = false,
}: ScrollToStageOptions = {}) => {
  const { isMobile } = useDevice();
  const { isDA, isGwat } = useTenant();
  const stageRef = useRef<T>(null);
  const shouldScrollOnMount = useRef(
    isMobile && (isDA || (isGwat && scrollOnGwat))
  );

  useEffect(() => {
    if (!shouldScrollOnMount.current) return;

    // Deferred so it runs after the app-level scroll-to-top effect that fires
    // on every path change, otherwise that one would win.
    const frame = window.requestAnimationFrame(() => {
      const stage = stageRef.current;
      if (!stage) return;

      const top =
        stage.getBoundingClientRect().top +
        window.pageYOffset -
        getHeaderHeight();

      window.scrollTo({
        top: Math.max(top, 0),
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      });
    });

    return () => window.cancelAnimationFrame(frame);
  }, []);

  return stageRef;
};

export { useScrollToStageOnMobile };
